"use client";

import * as React from "react";

import { cn } from "@/lib/utils";

type AnyProps = Record<string, unknown>;

function composeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === "function") ref(node);
      else (ref as React.MutableRefObject<T | null>).current = node;
    }
  };
}

function callAll<Args extends unknown[]>(
  ...fns: Array<((...args: Args) => void) | undefined>
): (...args: Args) => void {
  return (...args) => {
    for (const fn of fns) fn?.(...args);
  };
}

function mergeProps(slotProps: AnyProps, childProps: AnyProps) {
  const props: AnyProps = { ...slotProps, ...childProps };

  // Merge className
  if ("className" in slotProps || "className" in childProps) {
    props.className = cn(slotProps.className as string, childProps.className as string);
  }

  // Merge style
  if ("style" in slotProps || "style" in childProps) {
    props.style = { ...(slotProps.style as object), ...(childProps.style as object) };
  }

  // Merge common event handlers (slot then child)
  for (const key of Object.keys(slotProps)) {
    if (!/^on[A-Z]/.test(key)) continue;
    const slotFn = slotProps[key] as ((...a: unknown[]) => void) | undefined;
    const childFn = childProps[key] as ((...a: unknown[]) => void) | undefined;
    if (typeof slotFn === "function" && typeof childFn === "function") {
      props[key] = callAll(slotFn as never, childFn as never);
    }
  }

  return props;
}

export type SlotProps = React.HTMLAttributes<HTMLElement> & {
  children?: React.ReactElement;
};

/**
 * Minimal Radix-like Slot replacement used by existing shadcn-style `asChild` components.
 * - Clones exactly one child element and merges props/className/style/handlers.
 */
export const BaseSlot = React.forwardRef<HTMLElement, SlotProps>(({ children, ...slotProps }, ref) => {
  if (!React.isValidElement(children)) return null;

  const child = children as React.ReactElement & { ref?: React.Ref<unknown> };
  const merged = mergeProps(slotProps as AnyProps, child.props as AnyProps);
  const childRef = (child as React.ReactElement<unknown> & { ref?: React.Ref<unknown> }).ref;

  return React.cloneElement(child, {
    ...merged,
    ref: composeRefs(ref as React.Ref<unknown>, childRef),
  } as React.Attributes & typeof merged & { ref?: React.Ref<unknown> });
});

BaseSlot.displayName = "Slot";

/**
 * Base UI replacement for Radix `asChild`: provide a `render` function that composes with `children`.
 */
export function renderAsChild(children: React.ReactNode) {
  const child = React.Children.only(children) as React.ReactElement;
  return (props: AnyProps) => {
    const merged = mergeProps(props, child.props as AnyProps);
    return React.cloneElement(child, merged);
  };
}
